import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowDown,
  ArrowUpRight,
  MousePointerClick,
  Zap,
  Bot,
  Smartphone,
  Star,
  StarHalf,
  Target,
} from "lucide-react";
import { LovableLogo, EmergentLogo, LOVABLE_URL, EMERGENT_URL, scrollToId, EASE } from "./shared";

const MaskedLine = ({ i, children, className = "" }) => (
  <span className="block overflow-hidden pb-1.5 -mb-1.5">
    <motion.span
      custom={i}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: EASE }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Stars = ({ filled, half = false }) => (
  <span
    className="flex items-center gap-0.5"
    aria-label={`${half ? filled + 0.5 : filled} out of 5 stars`}
  >
    {Array.from({ length: 5 }).map((_, i) => {
      if (i < filled)
        return <Star key={i} className="h-3 w-3 fill-amber-300 text-amber-300" />;
      if (i === filled && half)
        return <StarHalf key={i} className="h-3 w-3 fill-amber-300 text-amber-300" />;
      return <Star key={i} className="h-3 w-3 text-slate-700" />;
    })}
  </span>
);

const MiniStat = ({ icon: Icon, label, accent }) => (
  <div className="flex items-center gap-2 rounded-lg border border-white/[0.07] bg-white/[0.03] px-3 py-2">
    <Icon
      className={`h-3 w-3 shrink-0 ${accent === "lov" ? "text-[#FF5C8A]" : "text-cyan-300"}`}
    />
    <span className="text-[11px] leading-snug text-slate-300">{label}</span>
  </div>
);

const HeroVisual = () => (
  <motion.div
    initial={{ opacity: 0, x: 56, scale: 0.97 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
    data-testid="hero-comparison-visual"
    className="relative mx-auto w-full max-w-lg"
  >
    <div className="pointer-events-none absolute -inset-8 rounded-[40px] bg-blue-600/10 blur-[60px]" />
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]/90 shadow-[0_40px_100px_-30px_rgba(59,130,246,0.3)] backdrop-blur-xl">
      <div className="flex items-center justify-center gap-2.5 border-b border-white/[0.07] px-5 py-3.5">
        <span className="h-1.5 w-1.5 rounded-full bg-[#FF5C8A]/70" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#FF5C8A]/35" />
        <span className="font-mono2 mx-1.5 text-[10px] uppercase tracking-[0.3em] text-slate-500">
          Head-to-head · 2026
        </span>
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/35" />
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/70" />
      </div>
      <div className="relative grid grid-cols-1 min-[430px]:grid-cols-2">
        <div className="flex flex-col border-b border-white/[0.07] bg-gradient-to-b from-[#FF2D55]/[0.06] to-transparent p-4 min-[430px]:border-b-0 min-[430px]:border-r min-[430px]:p-5">
          <div className="flex min-h-[38px] items-center gap-2.5">
            <LovableLogo size={34} />
            <div>
              <p className="font-display text-base font-bold leading-tight text-white">Lovable</p>
              <p className="hidden whitespace-nowrap text-[10px] text-slate-500 min-[430px]:block">
                Visual web builder
              </p>
            </div>
            <div
              className="ml-auto flex items-center gap-1.5 min-[430px]:hidden"
              data-testid="hero-visual-lovable-rating"
            >
              <Stars filled={4} half />
              <span className="text-[11px] font-bold text-amber-300">4.7/5</span>
            </div>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-500 min-[430px]:hidden">Visual web builder</p>
          <div className="mt-3 hidden items-center gap-2 min-[430px]:flex">
            <Stars filled={4} half />
            <span className="text-[11px] font-bold text-amber-300">4.7/5</span>
          </div>
          <div className="mt-3 space-y-2 border-t border-white/[0.07] pt-3">
            <MiniStat icon={Zap} label="First preview in ~2 min" accent="lov" />
            <MiniStat icon={MousePointerClick} label="Visual point-and-click editor" accent="lov" />
            <MiniStat icon={Target} label="Pro plan $25/month" accent="lov" />
          </div>
          <a
            data-testid="hero-visual-lovable-cta"
            href={LOVABLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 hidden items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#FF2D55] to-[#8B5CF6] px-3 py-2.5 text-xs font-bold text-white shadow-[0_0_20px_rgba(255,45,85,0.35)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(255,45,85,0.55)] md:flex"
          >
            Visit Lovable
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="flex flex-col bg-gradient-to-b from-cyan-400/[0.06] to-transparent p-4 min-[430px]:p-5">
          <div className="flex min-h-[38px] items-center gap-2.5">
            <EmergentLogo size={34} />
            <div>
              <p className="font-display text-base font-bold leading-tight text-white">Emergent</p>
              <p className="hidden whitespace-nowrap text-[10px] text-slate-500 min-[430px]:block">
                Full-stack AI engineer
              </p>
            </div>
            <div
              className="ml-auto flex items-center gap-1.5 min-[430px]:hidden"
              data-testid="hero-visual-emergent-rating"
            >
              <Stars filled={5} />
              <span className="text-[11px] font-bold text-amber-300">4.8/5</span>
            </div>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-500 min-[430px]:hidden">
            Full-stack AI engineer
          </p>
          <div className="mt-3 hidden items-center gap-2 min-[430px]:flex">
            <Stars filled={5} />
            <span className="text-[11px] font-bold text-amber-300">4.8/5</span>
          </div>
          <div className="mt-3 space-y-2 border-t border-white/[0.07] pt-3">
            <MiniStat icon={Bot} label="Full-stack app in ~4 min" accent="emg" />
            <MiniStat icon={Smartphone} label="Web, iOS & Android" accent="emg" />
            <MiniStat icon={Target} label="Standard plan $20/month" accent="emg" />
          </div>
          <a
            data-testid="hero-visual-emergent-cta"
            href={EMERGENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 hidden items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 px-3 py-2.5 text-xs font-bold text-white shadow-[0_0_20px_rgba(34,211,238,0.35)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(34,211,238,0.55)] md:flex"
          >
            Visit Emergent
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-cyan-400/40 bg-[#050505] font-display text-sm font-extrabold text-white shadow-[0_0_30px_rgba(34,211,238,0.35)]">
            VS
          </div>
        </div>
      </div>
      <div className="hidden border-t border-white/[0.07] px-5 py-3 min-[430px]:block">
        <p className="font-mono2 text-center text-[10px] uppercase tracking-[0.25em] text-slate-600">
          10 categories · 12 lab builds · July 2026
        </p>
      </div>
    </div>
  </motion.div>
);

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const orbY1 = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section id="top" ref={sectionRef} className="relative overflow-hidden pt-24 pb-14 sm:pt-28">
      <div className="grid-bg pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_70%_55%_at_50%_0%,black,transparent)]" />
      <motion.div
        style={{ y: orbY1 }}
        className="pointer-events-none absolute left-[2%] top-16 h-56 w-56 rounded-full bg-blue-600/10 blur-[110px]"
      />
      <motion.div
        style={{ y: orbY2 }}
        className="pointer-events-none absolute right-[2%] top-56 h-48 w-48 rounded-full bg-cyan-400/[0.07] blur-[110px]"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="mb-7 flex items-center gap-4"
            >
              <span className="font-mono2 text-[11px] tracking-[0.35em] text-cyan-300/90">01</span>
              <span className="h-px w-10 bg-gradient-to-r from-cyan-400/70 to-transparent" />
              <span
                data-testid="hero-chapter-badge"
                className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-slate-500"
              >
                The 2026 Head-to-Head
              </span>
            </motion.div>

            <h1 className="font-display max-w-xl text-3xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
              <MaskedLine i={0}>Lovable vs Emergent:</MaskedLine>
              <MaskedLine i={1}>Which AI Builder Is</MaskedLine>
              <MaskedLine i={2}>
                Better in <span className="text-gradient-cyan">2026?</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
              className="mt-6 max-w-xl text-base text-slate-400 md:text-lg"
            >
              We compared features, ease of use, speed, AI capabilities, pricing, and real-world
              performance — so you can pick the right builder in minutes, not weeks.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.78, ease: EASE }}
              className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <button
                data-testid="hero-see-comparison-btn"
                onClick={() => scrollToId("#comparison")}
                className="group flex w-full max-w-[320px] items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-7 py-3.5 text-sm font-bold text-white shadow-[0_0_35px_rgba(59,130,246,0.45)] transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_55px_rgba(34,211,238,0.55)] sm:w-auto"
              >
                See Full Comparison
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </button>
              <button
                data-testid="hero-skip-verdict-btn"
                onClick={() => scrollToId("#verdict")}
                className="w-full max-w-[320px] rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:border-cyan-400/50 hover:text-white sm:w-auto"
              >
                Skip to Verdict
              </button>
            </motion.div>
          </div>

          <HeroVisual />
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 max-[360px]:grid-cols-1 md:hidden">
          <a
            data-testid="hero-mobile-lovable-cta"
            href={LOVABLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-[#FF2D55]/50 bg-[#FF2D55]/10 px-3 py-2.5 text-xs font-bold text-white shadow-[0_0_25px_rgba(255,45,85,0.3)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_0_40px_rgba(255,45,85,0.5)]"
          >
            Visit Lovable
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <a
            data-testid="hero-mobile-emergent-cta"
            href={EMERGENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-cyan-400/50 bg-cyan-400/10 px-3 py-2.5 text-xs font-bold text-white shadow-[0_0_25px_rgba(34,211,238,0.3)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_0_40px_rgba(34,211,238,0.5)]"
          >
            Visit Emergent
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
